/** Minimal markdown-to-HTML renderer for recipe readmes.
 *
 *  Supports a safe subset — no raw HTML passthrough. All output is
 *  constructed from known safe elements. User text within elements
 *  is escaped via the `esc` function.
 *
 *  Supported syntax:
 *    ## Headings (h2-h4)
 *    **bold** and *italic*
 *    `inline code` and ```fenced code blocks```
 *    - Unordered lists
 *    1. Ordered lists
 *    | Tables |
 *    [links](url)
 *    > Blockquotes
 *    --- Horizontal rules
 *    Paragraphs (blank line separated)
 */

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ESC[c] ?? c);

/** Render markdown to sanitized HTML. */
export const renderMarkdown = (md: string): string => {
  const lines = md.split('\n');
  const out: string[] = [];
  let i = 0;
  let inList: 'ul' | 'ol' | null = null;

  const closeList = () => {
    if (inList) { out.push(`</${inList}>`); inList = null; }
  };

  while (i < lines.length) {
    // ⛔ PROGRESS ASSERTION. Every branch below must consume at least one line.
    // When one does not, `i` never advances and this loop spins forever — the
    // build does not fail, it HANGS, with the last log line being whatever ran
    // before it. That cost a wedged deploy: `docs/public/support.md` grew a
    // second `# H1`, which the heading branch rejects (it matches `#{2,4}` — the
    // page template supplies the h1) and which the paragraph accumulator also
    // refuses (it stops at any line starting with `#`). No branch owned the
    // line, so nothing moved.
    //
    // 🔑 Guarding the CLASS, not that one character. Any future construct that
    // slips between the branches now names itself instead of freezing CI, and a
    // hang is the one failure mode that tells you nothing at all.
    const lineAtEntry = i;
    const line = lines[i];

    // Fenced code block
    if (line.startsWith('```')) {
      closeList();
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(esc(lines[i]));
        i++;
      }
      i++; // skip closing ```
      out.push(`<pre${lang ? ` class="lang-${esc(lang)}"` : ''}><code>${codeLines.join('\n')}</code></pre>`);
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      closeList();
      out.push('<hr/>');
      i++;
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{2,4})\s+(.+)$/);
    if (headingMatch) {
      closeList();
      const level = headingMatch[1].length;
      out.push(`<h${level}>${inline(headingMatch[2])}</h${level}>`);
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      closeList();
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(inline(lines[i].slice(2)));
        i++;
      }
      out.push(`<blockquote>${quoteLines.join('<br/>')}</blockquote>`);
      continue;
    }

    // Table
    if (line.includes('|') && line.trim().startsWith('|')) {
      closeList();
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      out.push(renderTable(tableLines));
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      if (inList !== 'ul') { closeList(); out.push('<ul>'); inList = 'ul'; }
      out.push(`<li>${inline(line.replace(/^[-*]\s+/, ''))}</li>`);
      i++;
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      if (inList !== 'ol') { closeList(); out.push('<ol>'); inList = 'ol'; }
      out.push(`<li>${inline(line.replace(/^\d+\.\s+/, ''))}</li>`);
      i++;
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      closeList();
      i++;
      continue;
    }

    // Paragraph — collect consecutive non-blank lines
    closeList();
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#') && !lines[i].startsWith('```') && !lines[i].startsWith('> ') && !/^[-*]\s+/.test(lines[i]) && !/^\d+\.\s+/.test(lines[i]) && !(lines[i].includes('|') && lines[i].trim().startsWith('|'))) {
      paraLines.push(inline(lines[i]));
      i++;
    }
    if (paraLines.length > 0) {
      out.push(`<p>${paraLines.join(' ')}</p>`);
    }

    if (i === lineAtEntry) {
      // ⚠ Name the line and say what to do. The one previous instance was a
      // lone `#`, and "supported headings are ## to ####" is the sentence that
      // would have saved the whole diagnosis.
      throw new Error(
        `renderMarkdown: no rule consumed line ${lineAtEntry + 1}, so rendering could not `
          + `progress: ${JSON.stringify(lines[lineAtEntry])}\n`
          + '  Headings are ## to #### — a single # is not supported, because the page '
          + 'template already emits the <h1>. Demote it, or teach a branch to consume it.',
      );
    }
  }

  closeList();
  return out.join('\n');
};

/** Render inline markdown: bold, italic, code, links. All text is escaped. */
const inline = (text: string): string => {
  let result = esc(text);
  // Inline code (before bold/italic to avoid conflicts)
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');
  // Bold
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic
  result = result.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // Links — allow http/https (external, new tab), mailto:, and root-relative
  // `/path` (same-origin apex nav; a single leading slash, never `//`). Anything
  // else (javascript:, protocol-relative //, bare words) is left as literal text.
  result = result.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+|mailto:[^)\s]+|\/(?!\/)[^)\s]*)\)/g,
    (_m, label: string, href: string) => {
      const attrs = /^https?:\/\//.test(href) ? ' target="_blank" rel="noopener"' : '';
      return `<a href="${href}"${attrs}>${label}</a>`;
    },
  );
  return result;
};

/** Render a markdown table (pipe-delimited rows). */
const renderTable = (lines: string[]): string => {
  const parseRow = (line: string): string[] =>
    line.split('|').map((c) => c.trim()).filter((c, i, a) => i > 0 && i < a.length - (line.endsWith('|') ? 1 : 0));

  if (lines.length < 2) return '';

  const headerCells = parseRow(lines[0]);
  // Skip separator line (|---|---|)
  const startRow = lines[1].includes('---') ? 2 : 1;
  const bodyRows = lines.slice(startRow);

  let html = '<table><thead><tr>';
  for (const cell of headerCells) {
    html += `<th>${inline(cell)}</th>`;
  }
  html += '</tr></thead><tbody>';
  for (const row of bodyRows) {
    const cells = parseRow(row);
    html += '<tr>';
    for (const cell of cells) {
      html += `<td>${inline(cell)}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table>';
  return html;
};
